/**
 * The two directions between what somebody types and what is stored.
 *
 * Shared rather than living in the composer, because editing has to be able to
 * get back to the text that was typed. A round trip that is not quite the
 * inverse shows somebody `<p>` tags in the box they are trying to fix a typo
 * in, which is worse than not offering the edit.
 *
 * The CRM composer is a textarea rather than the rich editor the Helpdesk uses
 * on tickets: a CRM note is two sentences a rep types between calls, and the
 * formatting toolbar is furniture in the way of that. Line breaks are the only
 * structure anybody uses, so they are the only structure carried across.
 */

/**
 * Plain text into the minimal HTML the timeline renders.
 *
 * Escaped first, because what is typed is text and must not become markup on
 * the way in -- the server sanitises as well, but a value that only survives
 * because something downstream cleans it is a value being written wrongly.
 */
export function toHtml(text: string): string {
  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  return escaped
    .split(/\n{2,}/)
    .map((block) => `<p>${block.replace(/\n/g, "<br>")}</p>`)
    .join("");
}

/**
 * Stored HTML back to the text somebody would have typed.
 *
 * Paragraph breaks become blank lines and `<br>` becomes a newline, then the
 * browser turns whatever is left into text -- which also unescapes the
 * entities `toHtml` introduced, and copes with markup this never wrote, since
 * a body can arrive from an ingest or an API caller rather than the composer.
 *
 * Parsing through a detached element rather than by regex. Nothing here is
 * rendered or attached, only `textContent` is read, and stripping tags with a
 * regex is the classic way to be wrong about nested markup.
 */
export function toText(html: string): string {
  const host = document.createElement("div");
  host.innerHTML = html
    .replace(/<\/p>\s*<p[^>]*>/gi, "\n\n")
    .replace(/<br\s*\/?>/gi, "\n");

  return (host.textContent ?? "").trim();
}
